import { useState } from 'react';
import { Button, Modal, Typography } from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import { WORKBENCH_SHORTCUTS, type ShortcutScope } from './keymap';

const { Text, Paragraph } = Typography;

const SCOPE_TITLE: Record<ShortcutScope, string> = {
  queue: 'In the queue',
  drawer: 'In an open item',
  global: 'Anywhere',
};

const SCOPE_ORDER: ShortcutScope[] = ['queue', 'drawer', 'global'];

/**
 * The Shortcuts reference — rendered FROM the keymap table that the handlers resolve through, so
 * this dialog can never document a chord that does not fire.
 *
 * It exists because discoverability is the failure mode of keyboard-first design: an analyst who
 * never learns `1`–`9` works the queue at mouse speed forever. The trigger sits next to the queue
 * where the work is, not buried in a help menu.
 */
export function ShortcutsReference() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button size="small" type="text" onClick={() => setOpen(true)} aria-haspopup="dialog">
        <span className="ol-kbd" aria-hidden>
          ⌨
        </span>{' '}
        Shortcuts
      </Button>
      <Modal
        open={open}
        onCancel={() => setOpen(false)}
        footer={null}
        width={520}
        title={
          <span>
            <ThunderboltOutlined style={{ marginInlineEnd: 8, color: 'var(--ol-primary)' }} />
            Keyboard shortcuts
          </span>
        }
      >
        <Paragraph type="secondary" style={{ fontSize: 12.5, marginTop: -4 }}>
          Single keys work whenever you are not typing in a field — the moment focus is in a text
          box, every one of them stands down and types instead.
        </Paragraph>
        {SCOPE_ORDER.map((scope) => (
          <div key={scope} style={{ marginBottom: 14 }}>
            <div className="ol-section">{SCOPE_TITLE[scope]}</div>
            <table className="ol-shortcuts">
              <tbody>
                {WORKBENCH_SHORTCUTS.filter((s) => s.scope === scope).map((s) => (
                  <tr key={s.label}>
                    <td className="ol-shortcuts__keys">
                      {s.keys.map((k) => (
                        <span key={k} className="ol-kbd">
                          {k}
                        </span>
                      ))}
                    </td>
                    <td>
                      <Text style={{ fontSize: 12.5 }}>{s.label}</Text>
                      {s.note && (
                        <div style={{ fontSize: 11.5, color: 'var(--ol-text-tertiary)', lineHeight: 1.45 }}>
                          {s.note}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </Modal>
    </>
  );
}
